(function (scope) {
  const cleanText = value => String(value ?? '').replace(/<\/?tc\s*>/gi, '').trim();
  const normalize = value => String(value || '').toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
  const localizedGarments = [
    ['accessories', /\b(?:echarpes?|foulards?|bonnets?|ceintures?|sacs?|chaussettes?|sjaals?|muts(?:en)?|riem(?:en)?|tas(?:sen)?|sok(?:ken)?)\b/],
    ['jackets', /\b(?:vestes?|manteaux?|blousons?|jas(?:sen|je|jes)?|jacks?|mantels?)\b/],
    ['dresses', /\b(?:robes?|combinaisons?|salopettes?|jurk(?:en)?)\b/],
    ['skirts', /\b(?:jupes?|rok(?:ken)?)\b/],
    ['jeans', /\bjean\b/],
    ['pants', /\b(?:pantalons?|broek(?:en)?)\b/],
    ['knitwear', /\b(?:pulls?|gilets?|tricot|trui(?:en)?|vest(?:en)?)\b/],
    ['tops', /\b(?:chemises?|chemisiers?|hauts?|debardeurs?|hemd(?:en)?|topjes?)\b/]
  ];
  const shortProductTitle = product => {
    const title = String(product?.title || '').trim().replace(/\s+/g, ' ');
    const type = String(product?.type || '').trim().replace(/\s+/g, ' ');
    const noun = text => (text.match(/T[ -]?Shirts?|[\p{L}\p{M}]+/giu) || []).find(word =>
      localizedGarments.some(([, pattern]) => pattern.test(normalize(word))) ||
      /^(?:T[ -]?shirts?|sweaters?|sweatshirts?|hoodies?|shirts?|tops?|blouses?|dress(?:es)?|skirts?|jeans|trousers?|pants|shorts|leggings|culottes?|jackets?|coats?|blazers?|vests?|overshirts?|cardigans?|pullovers?|jumpsuits?|overalls?|scarves|scarf|beanies?|belts?|bags?|handbags?|socks?|tunika|poncho|kimono|oberteil|sweat)$/iu.test(word) ||
      /^[\p{L}\p{M}]*(?:shirt|bluse|kleid|rock|hose|jacke|mantel|weste|pullover|cardigan|schal|mütze|gürtel|tasche|socken)$/iu.test(word));
    if (!title) return noun(type) || type.split(' ').slice(0, 3).join(' ');
    const words = title.split(' '), model = words[0];
    const hasModel = /[\p{L}]/u.test(model) && model === model.toUpperCase() &&
      /^[\p{L}\p{M}\d-]+$/u.test(model) && !noun(model);
    if (!hasModel) return words.slice(0, 3).join(' ');
    const garment = noun(words.slice(1).join(' ')) || noun(type);
    return garment ? `${model} ${garment}` : words.slice(0, 3).join(' ');
  };
  const category = product => {
    const text = normalize([product.type, product.title, ...(product.tags || [])].join(' '));
    // The first local garment noun wins: a robe with a ceinture remains a dress.
    const localized = localizedGarments.map(([key, pattern]) => ({key, match: pattern.exec(text)}))
      .filter(entry => entry.match).sort((a, b) => a.match.index - b.match.index)[0];
    if (localized) return localized.key;
    for (const [key, pattern] of [
      ['accessories', /schal|scarf|mutze|beanie|gurtel|belt|tasche|bag\b|accessoire|sock/],
      ['jackets', /jacke|jacket|mantel|coat|blazer|weste|trench|overshirt/],
      ['dresses', /kleid|dress|jumpsuit|overall/],
      ['skirts', /\brock\b|skirt/],
      ['jeans', /jeans/],
      ['pants', /hose|trouser|pants|culotte|shorts|leggings/],
      ['knitwear', /strick|knit|cardigan|pullover/],
      ['tops', /bluse|blouse|shirt|sweater|sweat|hoodie|oberteil|\btop\b|tunika/]
    ]) if (pattern.test(text)) return key;
    return 'other';
  };
  const colorNames = value => {
    const text = normalize(value);
    const families = [
      ['Schwarz', /black|schwarz|noir|\bzwart(?:e)?\b/], ['Weiß', /white|weiss|weiß|ivory|natur|ecru|creme|cream|\b(?:blanc(?:s|he|hes)?|wit(?:te)?|ivoire|ivoor)\b/],
      ['Grau', /gr[ae]y|grau|anthra|silver|silber|\b(?:gris(?:e|es)?|grijs|grijze|argent|zilver)\b/], ['Beige', /beige|sand|taupe|stone|\b(?:sable|zand|zandkleurig(?:e)?)\b/],
      ['Braun', /brown|braun|chocolate|walnut|mocha|cacao|cognac|teak|tabak|tobacco|mud|\b(?:marrons?|brun(?:e|s|es)?|bruin(?:e)?|chocolat|chocolade)\b/],
      ['Blau', /blue|blau|navy|denim|indigo|\b(?:bleu(?:e|s|es)?|marine)\b/], ['Grün', /green|grun|olive|khaki|sage|mint|pistach|\b(?:vert(?:e|s|es)?|groen(?:e)?)\b/],
      ['Rot', /\bred\b|rot|karmin|salsa|burgund|bordeaux|wine|cherry|\b(?:rouges?|rood|rode)\b/],
      ['Rosa', /pink|rosa|rose|fuchsia|\broze\b/], ['Lila', /purple|lila|violet|lavend|\b(?:paars|paarse|mauve)\b/],
      ['Gelb', /yellow|gelb|lemon|\b(?:jaunes?|geel|gele)\b/], ['Orange', /orange|apricot|peach|\boranje\b/]
    ];
    const found = families.filter(([,pattern]) => pattern.test(text)).map(([label]) => label);
    return found.length ? found : value ? [String(value).trim()] : [];
  };
  const prepare = product => {
    const options = product.options.map(cleanText);
    const variants = product.variants.map(variant => ({...variant, options: variant.options.map(cleanText)}));
    const sizeIndex = options.findIndex(name => /gro(?:ss|ß)e|size|taille|\bmaat\b/.test(normalize(name)));
    const colorIndex = options.findIndex(name => /farbe|colou?r|couleur|\bkleur\b/.test(normalize(name)));
    return {
      ...product,
      options,
      variants,
      category: category(product),
      sizeIndex,
      colors: [...new Set(colorIndex < 0 ? colorNames(product.colorText) : variants.flatMap(v => colorNames(v.options[colorIndex]))) ],
      sizes: sizeIndex < 0 ? [] : [...new Set(variants.filter(v => v.available).map(v => v.options[sizeIndex]).filter(Boolean))]
    };
  };
  const matches = (product, filter) => (!filter.available || product.available) &&
    (!filter.category || product.category === filter.category) &&
    (!filter.color || product.colors.includes(filter.color)) &&
    (!filter.size || product.sizes.includes(filter.size));
  const hash = text => {
    let value = 2166136261;
    for (let i = 0; i < text.length; i++) value = Math.imul(value ^ text.charCodeAt(i), 16777619);
    return (value >>> 0) / 4294967296;
  };
  const rank = (products, history, filter, seed, savedHandles = [], recent = []) => {
    const categories = {}, colors = {};
    for (const product of products) {
      const action = history[product.id];
      const weight = savedHandles.includes(product.handle) ? 2 : action?.kind === 'like' ? 1 : action?.kind === 'skip' ? -0.15 : 0;
      if (!weight) continue;
      categories[product.category] = (categories[product.category] || 0) + weight;
      for (const color of product.colors) colors[color] = (colors[color] || 0) + weight;
    }
    const exploration = Object.keys(history).length % 4 === 3;
    return products.filter(p => matches(p, filter) && !history[p.id]).map(product => {
      let score = hash(`${seed}:${product.id}`);
      if (filter.mode !== 'shuffle') {
        if (!exploration) score += Math.tanh((categories[product.category] || 0) / 4) * 0.8 + product.colors.reduce((sum, color) => sum + Math.tanh((colors[color] || 0) / 4) * 0.35, 0);
        if (recent.slice(-2).includes(product.category)) score -= 0.5;
      }
      return {product, score};
    }).sort((a, b) => b.score - a.score || a.product.id.localeCompare(b.product.id)).map(entry => entry.product);
  };
  const mergeProducts = (existing, incoming) => {
    const map = new Map(existing.map(p => [p.id, p]));
    for (const product of incoming) {
      if (normalize(product.vendor).replace(/\s+/g, '') !== 'kultfrau' || product.giftCard || !product.images?.length) continue;
      map.set(product.id, prepare(product));
    }
    return [...map.values()];
  };
  const api = {cleanText, normalize, shortProductTitle, category, colorNames, prepare, matches, hash, rank, mergeProducts};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else scope.KultfrauDiscoveryCore = api;
})(typeof window === 'undefined' ? globalThis : window);
